import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  SentimentSatisfied as SentimentIcon,
  Assignment as ActionIcon,
  Download as ExportIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import SprintRetrospective from '../components/Retrospective/SprintRetrospective';
import RetrospectiveSession from '../components/Retrospective/RetrospectiveSession';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Retrospective {
  id: string;
  title: string;
  description?: string;
  template: string;
  status: 'draft' | 'in_progress' | 'completed' | 'cancelled';
  sprintId: string;
  facilitatorId: string;
  responses?: { [categoryId: string]: any[] };
  summary?: {
    keyInsights: string[];
    actionItemsCount: number;
    sentimentScore: number;
    participationRate: number;
    completedAt?: string;
  };
  scheduledAt?: string;
  completedAt?: string;
  durationMinutes: number;
  settings: {
    allowAnonymous: boolean;
    allowVoting: boolean;
    maxVotesPerPerson: number;
    requireAllParticipants: boolean;
    autoCompleteAfterHours: number;
  };
  facilitator: {
    id: string;
    firstName: string;
    lastName: string;
  };
  sprint?: {
    id: string;
    name: string;
    project: {
      id: string;
      name: string;
    };
  };
  actionItems?: any[];
  sentiments?: any[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`retrospective-tabpanel-${index}`}
      aria-labelledby={`retrospective-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Retrospectives: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [retrospectives, setRetrospectives] = useState<Retrospective[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  
  // Modal states
  const [openSessionModal, setOpenSessionModal] = useState(false);
  const [selectedRetrospective, setSelectedRetrospective] = useState<Retrospective | null>(null);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);

  const loadRetrospectives = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get retrospectives for all projects the user has access to
      const isAdmin = user?.role && ['admin', 'super_manager'].includes(user.role);
      const projects = isAdmin 
        ? await apiService.getProjects()
        : await apiService.getUserProjects();
      
      const allRetrospectives: Retrospective[] = [];
      
      for (const project of projects) {
        try {
          const response = await apiService.get<Retrospective[]>(`/retrospectives/project/${project.id}`);
          if (response.success && response.data) {
            allRetrospectives.push(...response.data);
          }
        } catch (err) {
          console.error(`Failed to load retrospectives for project ${project.name}:`, err);
        }
      }
      
      setRetrospectives(allRetrospectives);
    } catch (err: any) {
      console.error('Error loading retrospectives:', err);
      setError(err.response?.data?.message || 'Failed to load retrospectives');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadRetrospectives();
    
    // Check if we should open a specific retrospective session
    const sessionId = searchParams.get('session');
    if (sessionId) {
      // Load and open the specific retrospective
      handleOpenSession(sessionId);
    }
  }, [loadRetrospectives, searchParams]);

  const handleOpenSession = async (retrospectiveId: string) => {
    try {
      const response = await apiService.get<Retrospective>(`/retrospectives/${retrospectiveId}`);
      if (response.success && response.data) {
        setSelectedRetrospective(response.data);
        setOpenSessionModal(true);
      }
    } catch (err: any) {
      console.error('Error loading retrospective:', err);
      setError(err.response?.data?.message || 'Failed to load retrospective');
    }
  };

  const handleCloseSession = () => {
    setOpenSessionModal(false);
    setSelectedRetrospective(null);
    // Remove session parameter from URL
    navigate('/retrospectives', { replace: true });
  };

  const handleRetrospectiveComplete = (retrospective: Retrospective) => {
    // Refresh the retrospectives list
    loadRetrospectives();
    // Close the session modal
    handleCloseSession();
  };

  const handleSprintSelect = (sprintId: string) => {
    setSelectedSprintId(sprintId);
    setTabValue(1); // Switch to the sprint retrospectives tab
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <ViewIcon />;
      case 'in_progress': return <StartIcon />;
      case 'completed': return <CompleteIcon />;
      case 'cancelled': return <CloseIcon />;
      default: return <ViewIcon />;
    }
  };

  const getTemplateName = (template: string) => {
    const templates: { [key: string]: string } = {
      'start_stop_continue': 'Start, Stop, Continue',
      'four_ls': '4Ls',
      'mad_sad_glad': 'Mad, Sad, Glad',
      'kalm': 'KALM',
      'rose_thorn_bud': 'Rose, Thorn, Bud',
      'custom': 'Custom'
    };
    return templates[template] || template;
  };

  const filteredRetrospectives = retrospectives.filter(retrospective => {
    const matchesSearch = retrospective.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         retrospective.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         retrospective.sprint?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         retrospective.sprint?.project.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || retrospective.status === statusFilter;
    const matchesTemplate = templateFilter === 'all' || retrospective.template === templateFilter;
    
    return matchesSearch && matchesStatus && matchesTemplate;
  });

  const getRetrospectiveStats = () => {
    const total = retrospectives.length;
    const completed = retrospectives.filter(r => r.status === 'completed').length;
    const inProgress = retrospectives.filter(r => r.status === 'in_progress').length;
    const totalActionItems = retrospectives.reduce((sum, r) => sum + (r.summary?.actionItemsCount || 0), 0);
    const avgSentiment = retrospectives
      .filter(r => r.summary?.sentimentScore)
      .reduce((sum, r) => sum + (r.summary?.sentimentScore || 0), 0) / 
      retrospectives.filter(r => r.summary?.sentimentScore).length || 0;

    return { total, completed, inProgress, totalActionItems, avgSentiment };
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const stats = getRetrospectiveStats();

  return (
    <Layout>
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Sprint Retrospectives
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Reflect on sprints, gather team feedback, and create action items for continuous improvement.
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" color="primary">
                      {stats.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Retrospectives
                    </Typography>
                  </Box>
                  <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" color="success.main">
                      {stats.completed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                  <CompleteIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" color="warning.main">
                      {stats.totalActionItems}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Action Items
                    </Typography>
                  </Box>
                  <ActionIcon color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h4" color="info.main">
                      {stats.avgSentiment.toFixed(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Sentiment
                    </Typography>
                  </Box>
                  <SentimentIcon color="info" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="All Retrospectives" />
            <Tab label="Sprint Retrospectives" />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search retrospectives..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={templateFilter}
                    label="Template"
                    onChange={(e) => setTemplateFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Templates</MenuItem>
                    <MenuItem value="start_stop_continue">Start, Stop, Continue</MenuItem>
                    <MenuItem value="four_ls">4Ls</MenuItem>
                    <MenuItem value="mad_sad_glad">Mad, Sad, Glad</MenuItem>
                    <MenuItem value="kalm">KALM</MenuItem>
                    <MenuItem value="rose_thorn_bud">Rose, Thorn, Bud</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadRetrospectives}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                      setTemplateFilter('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Retrospectives List */}
          {filteredRetrospectives.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No retrospectives found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {searchTerm || statusFilter !== 'all' || templateFilter !== 'all'
                    ? 'Try adjusting your search criteria or clear the filters.'
                    : 'Create your first retrospective to start gathering team feedback and insights.'
                  }
                </Typography>
                {(!searchTerm && statusFilter === 'all' && templateFilter === 'all') && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setTabValue(1)}
                  >
                    Create First Retrospective
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {filteredRetrospectives.map((retrospective) => (
                <Grid item xs={12} md={6} lg={4} key={retrospective.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Typography variant="h6" component="h3" noWrap>
                          {retrospective.title}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(retrospective.status)}
                          label={retrospective.status.replace('_', ' ')}
                          color={getStatusColor(retrospective.status) as any}
                          size="small"
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {retrospective.description || 'No description provided'}
                      </Typography>

                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Chip
                          label={getTemplateName(retrospective.template)}
                          size="small"
                          variant="outlined"
                        />
                        {retrospective.sprint && (
                          <Chip
                            label={`${retrospective.sprint.project.name} - ${retrospective.sprint.name}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        {retrospective.scheduledAt && (
                          <Chip
                            icon={<ScheduleIcon />}
                            label={new Date(retrospective.scheduledAt).toLocaleDateString()}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      {/* Summary Stats */}
                      {retrospective.summary && (
                        <Box mb={2}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Action Items
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {retrospective.summary.actionItemsCount}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Participation
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {Math.round(retrospective.summary.participationRate)}%
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      )}

                      {/* Facilitator */}
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Avatar sx={{ width: 24, height: 24 }}>
                          {retrospective.facilitator.firstName[0]}{retrospective.facilitator.lastName[0]}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          Facilitated by {retrospective.facilitator.firstName} {retrospective.facilitator.lastName}
                        </Typography>
                      </Box>
                    </CardContent>

                    <Box sx={{ p: 2, pt: 0 }}>
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          startIcon={<ViewIcon />}
                          onClick={() => handleOpenSession(retrospective.id)}
                        >
                          View
                        </Button>
                        {retrospective.status === 'draft' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<StartIcon />}
                            onClick={() => handleOpenSession(retrospective.id)}
                          >
                            Start
                          </Button>
                        )}
                        {retrospective.status === 'in_progress' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CompleteIcon />}
                            onClick={() => handleOpenSession(retrospective.id)}
                          >
                            Continue
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {selectedSprintId ? (
            <SprintRetrospective
              sprintId={selectedSprintId}
              onRetrospectiveComplete={handleRetrospectiveComplete}
            />
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a Sprint
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose a sprint to view or create retrospectives for that specific sprint.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/sprints')}
                >
                  Go to Sprints
                </Button>
              </CardContent>
            </Card>
          )}
        </TabPanel>

        {/* Retrospective Session Modal */}
        <Dialog 
          open={openSessionModal} 
          onClose={handleCloseSession} 
          maxWidth="lg" 
          fullWidth
          fullScreen
        >
          <DialogTitle>
            {selectedRetrospective?.title}
            <IconButton
              aria-label="close"
              onClick={handleCloseSession}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedRetrospective && (
              <RetrospectiveSession
                retrospective={selectedRetrospective}
                onComplete={handleRetrospectiveComplete}
                onClose={handleCloseSession}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Retrospectives;
